import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { StorageAPIService, StorageListResult } from 'app/providers/storage-api.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import {
  RealtimeConnection,
  RealtimeEventHandler,
  RealtimeMessage,
  RealtimeService,
} from '../realtime.service';
import { OriginTypes } from '../semantria-api/semantria-account.service';
import { DocumentCountLookup } from './analysis.service';
import { ObjectLabel, Sideloadable } from './object-label.service';
import { urlFor } from './utils';

export interface Collection {
  collectionName: string;
  displayName: string;
  analysisCount: number;
  dataSource: string;
  documentCount: number;
  uploadCount: number;
  indexedAt: Date;
  projectId?: string;
  isAutoAnalyze?: boolean;
  configId?: string;
  analysisId?: string;
  isDriverAnalysis?: boolean;
  driverAnalysisAt?: Date;
  locked?: boolean;
  summarySceneId?: string;
  hasSections: boolean;
  sectionFieldNames: string[];
  sectionSeparator: SectionSeparator;
  focusedSpeakerName?: string;
  notes: string;
  isSocial?: boolean;
  origin?: OriginTypes[];
  calculatedFields: string[];
  contentChannel?: string;
  minDocAt?: Date;
  maxDocAt?: Date;
  objectLabels?: string[] | ObjectLabel[];
}

export interface SectionSeparator {
  label: string;
  displayAs: string;
  speakerName?: string;
}

export interface CollectionCreateOptions {
  name: string;
  isAutoAnalyze?: boolean;
  configId?: string;
  summarySceneId?: string;
  notes?: string;
  origin?: string;
  contentChannel?: string;
}

export interface CollectionUpdateOptions extends CollectionCreateOptions {
  sectionSeparator: SectionSeparator;
  calculatedFields: string[];
  focusedSpeakerName?: string;
}

export interface CollectionName {
  id: string;
  name: string;
  isAutoAnalyze: boolean;
  isDriverAnalysis: boolean;
  hasSections: boolean;
  sectionFieldNames: string[];
  calculatedFields: string[];
  objectLabels?: string[] | ObjectLabel[];
  origin?: string[];
  contentChannel?: string;
}

export interface SectionMetadataCardinality {
  fieldName: string;
  cardinality: number;
}

export interface SectionMetadata {
  fieldName: string;
  items: SectionMetadataBucket[];
  type?: SectionMetadataType;
}

export interface SectionMetadataAllReportResult {
  items: SectionMetadata[];
}

export type SectionMetadataType = 'metadata' | 'property';

export interface SectionMetadataBucket {
  value: string;
  count: number;
}

export interface CollectionUsages {
  analysisGroups: { id: string; name: string }[];
  dashboards: { id: string; name: string }[];
  hiddenDashboardCount: number;
}

export type CollectionSort = 'auto';
export type CollectionFilter = '';

@EnableClassTelemetry()
@Injectable()
export class CollectionService extends StorageAPIService {
  static map(item: Collection, labels?: ObjectLabel[]): Collection {
    if (item.indexedAt) {
      item.indexedAt = new Date(item.indexedAt);
    }

    if (item.objectLabels && labels) {
      item.objectLabels = item.objectLabels
        .map((cl) => labels.find((l) => cl === l.id))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'accent' }));
    }

    return item;
  }

  static mapCollectionToCollectionName(item: Collection): CollectionName {
    return {
      id: item.collectionName,
      name: item.displayName,
      isAutoAnalyze: item.isAutoAnalyze,
      isDriverAnalysis: item.isDriverAnalysis,
      hasSections: item.hasSections,
      sectionFieldNames: item.sectionFieldNames,
      calculatedFields: item.calculatedFields,
      objectLabels: item.objectLabels,
      origin: item.origin,
      contentChannel: item.contentChannel,
    };
  }

  constructor(
    http: HttpClient,
    systemService: SystemService,
    private realtimeService: RealtimeService
  ) {
    super(http, systemService);
  }

  on(namespace: string, fn: RealtimeEventHandler<Collection>): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        collection: (data: RealtimeMessage<Collection>) => {
          CollectionService.map(data.object);
          fn(data);
        },
      },
      'collectionService'
    );
  }

  async createCollection(
    projectId: string | undefined,
    options: CollectionCreateOptions
  ): Promise<Collection> {
    const endpoint = projectId ? `/projects/${projectId}/collections` : '/collections';
    return this.post<Collection>(endpoint, options);
  }

  async updateCollection(collection: Collection): Promise<Collection> {
    const options: CollectionUpdateOptions = {
      name: collection.displayName,
      isAutoAnalyze: collection.isAutoAnalyze,
      configId: collection.configId,
      summarySceneId: collection.summarySceneId,
      sectionSeparator: collection.sectionSeparator,
      focusedSpeakerName: collection.focusedSpeakerName,
      notes: collection.notes,
      calculatedFields: collection.calculatedFields,
      contentChannel: collection.contentChannel,
    };

    const projectId = collection.projectId;

    const endpoint =
      (projectId ? `/projects/${projectId}/collections` : '/collections') +
      `/${collection.collectionName}`;

    return this.patch<Collection>(endpoint, options);
  }

  getCollectionNames(projectId?: string): Promise<StorageListResult<CollectionName>> {
    const endpoint = projectId ? `/projects/${projectId}/collections` : '/collections';
    return this.get<StorageListResult<CollectionName>>(endpoint);
  }

  getCollections(projectId?: string): Promise<StorageListResult<Collection>> {
    return this.searchCollections(
      undefined,
      undefined,
      'auto',
      StorageAPIService.MAX_LIMIT,
      undefined,
      projectId
    );
  }

  async searchCollections(
    keyword?: string,
    filter?: CollectionFilter,
    _sort: CollectionSort = 'auto',
    limit: number = 20,
    pageToken?: string,
    projectId?: string
  ): Promise<StorageListResult<Collection>> {
    let queryString = '*';

    if (keyword && keyword[0] !== '"') {
      queryString = queryString.toLowerCase();
      queryString = keyword.replace(/[^a-zA-Z0-9_\-\*]/g, '');
      queryString = queryString.indexOf('*') >= 0 ? queryString : `*${queryString}*`;
    } else if (keyword) {
      queryString = keyword;
    }

    const query = queryString;
    const params = {
      detailed: 'true',
      query,
      'page-limit': limit,
    };
    if (pageToken && pageToken.trim().length > 1) {
      params['page-token'] = pageToken;
    }
    const result = await this.getPage<Collection>(urlFor(`/collections`, projectId), params);

    result.items = result.items.map((c) => CollectionService.map(c));

    return result;
  }

  async getCollectionByName(
    collectionName: string,
    projectId?: string,
    isSocial?: boolean,
    sideloads?: 'objectLabels'
  ): Promise<Collection> {
    const url = isSocial
      ? `/social/projects/${projectId}/collections/${collectionName}`
      : urlFor(`/collections/${collectionName}`, projectId);
    const params = sideloads ? { sideloads: 'objectLabels' } : {};

    return this.get<Sideloadable<Collection>>(url, params).then((response) =>
      CollectionService.map(response, response.sideloaded?.objectLabels)
    );
  }

  async clearCollectionsInProjectCache(projectId?: string): Promise<void> {
    return this.delete<void>(urlFor(`/projects/${projectId}/cache`), { clearAnalyses: 'true' });
  }

  async clearUserCache(): Promise<void> {
    return this.delete<void>(urlFor(`/user_cache`));
  }

  deleteCollection(collectionName: string): Promise<void> {
    return this.delete<void>(`/collections/${collectionName}`);
  }

  lockCollection(projectId: string, collectionName: string): Promise<void> {
    return this.post<void>(
      urlFor(`/projects/${projectId}/collections/${collectionName}/_lock`),
      {}
    );
  }

  unlockCollection(projectId: string, collectionName: string): Promise<void> {
    return this.post<void>(
      urlFor(`/projects/${projectId}/collections/${collectionName}/_unlock`),
      {}
    );
  }

  async getAutoAnalysisConfigs(): Promise<{ items: string[] }> {
    return this.get<{ items: string[] }>('/collections/_auto_analysis_configs');
  }

  getCollectionDocumentCount(projectId: string, lookupId: string): Promise<DocumentCountLookup> {
    const query = `publicId:_lookup_/${lookupId}`;
    return this.get(urlFor(`/projects/${projectId}/collections/_counts`), { query });
  }

  getSectionMetadataCardinality(
    projectId: string,
    collectionName: string
  ): Promise<StorageListResult<SectionMetadataCardinality>> {
    return this.get<StorageListResult<SectionMetadataCardinality>>(
      urlFor(`/projects/${projectId}/collections/${collectionName}/section-metadata/_cardinality`)
    );
  }

  getSectionMetadataTopN(
    projectId: string,
    collectionName: string,
    fieldName: string,
    bucketSize?: number
  ): Promise<SectionMetadata> {
    return this.get<SectionMetadata>(
      urlFor(`/projects/${projectId}/collections/${collectionName}/section-metadata/_topn`),
      {
        'field-name': fieldName,
        'bucket-size': bucketSize,
      }
    );
  }

  getSectionMetadataAllReport(
    projectId: string,
    collectionName: string,
    includes?: string[],
    excludes?: string[]
  ): Promise<SectionMetadataAllReportResult> {
    return this.get<SectionMetadataAllReportResult>(
      urlFor(`/projects/${projectId}/collections/${collectionName}/section-metadata/_all`),
      {
        includes,
        excludes,
      }
    );
  }

  getSectionPropertyCardinality(
    projectId: string,
    collectionName: string
  ): Promise<StorageListResult<SectionMetadataCardinality>> {
    return this.get<StorageListResult<SectionMetadataCardinality>>(
      urlFor(`/projects/${projectId}/collections/${collectionName}/section-properties/_cardinality`)
    );
  }

  getCollectionUsages(projectId: string, collectionName: string): Promise<CollectionUsages> {
    return this.get<CollectionUsages>(
      urlFor(`/projects/${projectId}/collections/${collectionName}/_usages`)
    );
  }

  async getSocialCollectionNames(projectId?: string): Promise<StorageListResult<CollectionName>> {
    let result;
    try {
      result = await this.get<StorageListResult<CollectionName>>(
        `/social/projects/${projectId}/social/collections`
      );
    } catch (e) {
      result = { items: [] };
    }

    // TODO: This should be coming from the API
    result.items = result.items.map((item) => {
      item['isSocial'] = true;
      item['id'] = item['collectionName'];
      return item;
    });

    return result;
  }

  async getSocialCollections(projectId?: string): Promise<StorageListResult<Collection>> {
    return this.searchSocialCollections(
      undefined,
      StorageAPIService.MAX_LIMIT,
      undefined,
      projectId
    );
  }

  // TODO: Should projectId be mandatory?
  private async searchSocialCollections(
    keyword?: string,
    limit: number = 20,
    pageToken?: string,
    projectId?: string
  ): Promise<StorageListResult<Collection>> {
    const params = this.getQueryParams(keyword, limit, pageToken);
    let result;
    try {
      result = await this.getPage<Collection>(
        `/social/projects/${projectId}/social/collections`,
        params
      );
    } catch (e) {
      result = { items: [] };
    }

    // TODO: This should be coming from the API
    result.items = result.items.map((item) => {
      item.isSocial = true;
      item['id'] = item.collectionName;
      item.analysisCount = 1;
      item.uploadCount = 1;
      item.analysisId = item['id'] + '_analysis';
      return item;
    });

    result.items = result.items.map((c) => CollectionService.map(c));

    return result;
  }

  private getQueryParams(keyword?: string, limit: number = 20, pageToken?: string) {
    let queryString = '*';

    if (keyword && keyword[0] !== '"') {
      queryString = keyword.toLowerCase().replace(/[^a-zA-Z0-9_\-\*]/g, '');
      if (!queryString.includes('*')) {
        queryString = `*${queryString}*`;
      }
    } else if (keyword) {
      queryString = keyword;
    }

    const params = {
      detailed: 'true',
      query: queryString,
      'page-limit': limit,
    };
    if (pageToken?.trim()) {
      params['page-token'] = pageToken;
    }

    return params;
  }
}
